import { Box, Chip, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { DashboardCollationItem } from '@/api/dashboardApi';
import { SectionCard } from '@/components/common';
import { formatDateShort as formatShortDate } from '@/utils/dateFormatter';
import { AppraisalStatusChip } from './AppraisalStatusChip';

function appraisalLabel(firstName: string, lastName: string): string {
  return `Appraisal \u2013 ${firstName} ${lastName}`;
}

function getNextDueDate(items: DashboardCollationItem[]): string | null {
  const dates = items
    .map((i) => i.collationDeadline)
    .filter((d): d is string => !!d)
    .map((d) => new Date(d))
    .filter((d) => !isNaN(d.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());
  return dates.length > 0 ? formatShortDate(dates[0].toISOString()) : null;
}

interface CollationSubsectionProps {
  collation: DashboardCollationItem[];
}

export function CollationSubsection({ collation }: CollationSubsectionProps) {
  const nextDue = getNextDueDate(collation);

  return (
    <SectionCard>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography component="h3" sx={{ typography: { xs: 'h6', md: 'h5' } }}>
          Collation
        </Typography>
        {nextDue && <Chip label={`Next due: ${nextDue}`} size="small" variant="outlined" />}
      </Box>
      {collation.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No collation responsibilities to display.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'custom.darkTeal', '& th': { color: 'common.white' } }}>
                <TableCell>APPRAISAL</TableCell>
                <TableCell>COLLATION</TableCell>
                <TableCell>EARLIEST DELIVERY</TableCell>
                <TableCell>STATUS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {collation.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Link component={RouterLink} to={`/appraisals/${item.id}`} underline="hover">
                      {appraisalLabel(item.appraiseeFirstName, item.appraiseeLastName)}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {item.collationDeadline ? (
                      <Chip label={`Due: ${formatShortDate(item.collationDeadline)}`} size="small" variant="outlined" />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{formatShortDate(item.earliestDeliveryDate)}</TableCell>
                  <TableCell>
                    <AppraisalStatusChip status={item.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </SectionCard>
  );
}

export default CollationSubsection;
